
import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { MemorialInfo } from "../MemorialCreationFlow";
import ProfilePictureUpload from "./ProfilePictureUpload";
import DatePickerField from "./DatePickerField";
import LocationField from "./LocationField";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

interface ProfileScreenProps {
  memorialInfo: MemorialInfo;
  onUpdate: (updates: Partial<MemorialInfo>) => void;
  onNext: () => void;
  isCreating?: boolean;
}

const ProfileScreen = ({
  memorialInfo,
  onUpdate,
  onNext,
  isCreating = false
}: ProfileScreenProps) => {
  const handleProfilePictureUpdate = (file: File) => {
    onUpdate({
      profilePicture: file
    });
  };

  // Handle date selection directly with string format
  const handleDateChange = (dateField: 'birthDate' | 'deathDate' | 'person2BirthDate' | 'person2DeathDate') => (dateString: string | undefined) => {
    onUpdate({
      [dateField]: dateString
    });
  };

  const fullName = `${memorialInfo.firstName} ${memorialInfo.lastName}`.trim();

  // Date validation logic
  const dateValidation = useMemo(() => {
    if (!memorialInfo.hasPassed || !memorialInfo.birthDate || !memorialInfo.deathDate) {
      return { isValid: true, errorMessage: null };
    }

    const birthDate = new Date(memorialInfo.birthDate);
    const deathDate = new Date(memorialInfo.deathDate);

    if (deathDate < birthDate) {
      return {
        isValid: false,
        errorMessage: "Date of death must be on or after the birth date."
      };
    }

    return { isValid: true, errorMessage: null };
  }, [memorialInfo.birthDate, memorialInfo.deathDate, memorialInfo.hasPassed]);

  // Person 2 date validation
  const person2DateValidation = useMemo(() => {
    if (!memorialInfo.isDoubleMemorial || !memorialInfo.person2BirthDate || !memorialInfo.person2DeathDate) {
      return { isValid: true, errorMessage: null };
    }

    const birthDate = new Date(memorialInfo.person2BirthDate);
    const deathDate = new Date(memorialInfo.person2DeathDate);

    if (deathDate < birthDate) {
      return {
        isValid: false,
        errorMessage: `${memorialInfo.person2FirstName || 'Second person'}'s date of death must be on or after their birth date.`
      };
    }

    return { isValid: true, errorMessage: null };
  }, [memorialInfo.person2BirthDate, memorialInfo.person2DeathDate, memorialInfo.isDoubleMemorial, memorialInfo.person2FirstName]);

  const person2Name = memorialInfo.person2FirstName || 'Second person';

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <h1 className="text-2xl font-serif text-center text-brand-text-primary text-brand-primary">
          Add details for {fullName}
        </h1>
        
        <ProfilePictureUpload profilePicture={memorialInfo.profilePicture} onUpdate={handleProfilePictureUpdate} />

        <div className="space-y-4">
          <DatePickerField 
            id="birthDate" 
            label="Birth Date" 
            value={memorialInfo.birthDate} 
            onChange={handleDateChange('birthDate')} 
            placeholder="Select birth date" 
          />

          <LocationField 
            id="birthPlace" 
            label="Place of Birth" 
            value={memorialInfo.birthPlace || ""} 
            onChange={value => onUpdate({ birthPlace: value })} 
            placeholder="Enter place of birth" 
          />

          {memorialInfo.hasPassed && (
            <>
              <DatePickerField
                id="deathDate"
                label="Date of Death"
                value={memorialInfo.deathDate}
                onChange={handleDateChange('deathDate')}
                placeholder="Select date of death"
              />

              <LocationField
                id="deathPlace"
                label="Place of Death"
                value={memorialInfo.deathPlace || ""}
                onChange={value => onUpdate({ deathPlace: value })}
                placeholder="Enter place of death"
              />
            </>
          )}
        </div>

        {/* Person 2 details for double memorials */}
        {memorialInfo.isDoubleMemorial && (
          <div className="space-y-4 pt-4">
            <div className="border-t border-gray-200 pt-4">
              <h2 className="text-xl font-serif text-brand-primary">
                Details for {person2Name} {memorialInfo.lastName}
              </h2>
            </div>

            <DatePickerField
              id="person2BirthDate"
              label="Birth Date"
              value={memorialInfo.person2BirthDate}
              onChange={handleDateChange('person2BirthDate')}
              placeholder="Select birth date"
            />

            <LocationField
              id="person2BirthPlace"
              label="Place of Birth"
              value={memorialInfo.person2BirthPlace || ""}
              onChange={value => onUpdate({ person2BirthPlace: value })}
              placeholder="Enter place of birth"
            />

            {memorialInfo.hasPassed && (
              <>
                <DatePickerField
                  id="person2DeathDate"
                  label="Date of Death"
                  value={memorialInfo.person2DeathDate}
                  onChange={handleDateChange('person2DeathDate')}
                  placeholder="Select date of death"
                />

                <LocationField
                  id="person2DeathPlace"
                  label="Place of Death"
                  value={memorialInfo.person2DeathPlace || ""}
                  onChange={value => onUpdate({ person2DeathPlace: value })}
                  placeholder="Enter place of death"
                />
              </>
            )}

            {/* Person 2 date validation error */}
            {!person2DateValidation.isValid && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {person2DateValidation.errorMessage}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>

      {/* Date validation error message */}
      {!dateValidation.isValid && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {dateValidation.errorMessage}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-2">
        <Button 
          type="button" 
          disabled={
            isCreating ||
            !memorialInfo.birthDate ||
            (memorialInfo.hasPassed && !memorialInfo.deathDate) ||
            !dateValidation.isValid ||
            !person2DateValidation.isValid
          } 
          onClick={onNext} 
          className="w-full bg-brand-primary"
        >
          {isCreating ? "Creating Memorial..." : "Create Memorial"}
        </Button>
      </div>
    </div>
  );
};

export default ProfileScreen;
